import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileSpreadsheet,
  Info,
  Upload,
  XCircle,
} from "lucide-react";
import { useCallback, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useM2CaricaPiano } from "@/hooks/useM2";
import { useMyPermessi } from "@/hooks/usePermessi";
import { ApiError, apiUploadFile } from "@/lib/api";
import { canUploadDati } from "@/lib/permessi";
import { cn } from "@/lib/utils";
import type { M1Snapshot, M1TipoFile } from "@/types/api";

interface TipoConfig {
  slug: M1TipoFile;
  label: string;
  hint: string;
  fileEsempio: string;
}

const TIPI: TipoConfig[] = [
  {
    slug: "ordini",
    label: "Ordini clienti",
    hint: "quotidiano",
    fileEsempio: "ordini clienti.xlsx",
  },
  {
    slug: "giacenze_pf",
    label: "Giacenze PF — E80 (BC 700)",
    hint: "quotidiano",
    fileEsempio: "E80.xls",
  },
  {
    slug: "giacenze_pf_terra",
    label: "Giacenze PF — a terra (BC 710)",
    hint: "quotidiano",
    fileEsempio: "PFTERRA.xls",
  },
  {
    slug: "piano_produzione",
    label: "Piano produzione",
    hint: "quotidiano",
    fileEsempio: "PIANO PRODUZIONE.xlsx",
  },
  {
    slug: "anagrafica_articoli",
    label: "Anagrafica articoli",
    hint: "settimanale",
    fileEsempio: "anagrafica articoli.xls",
  },
  {
    slug: "anagrafica_clienti",
    label: "Anagrafica clienti",
    hint: "settimanale",
    fileEsempio: "ANAGRAFICA CLIENTI.xlsx",
  },
];

export function UploadM1Dialog({ onClose }: { onClose: () => void }) {
  const { data: perms } = useMyPermessi();
  const canUpload = canUploadDati(perms);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Aggiorna dati Excel</DialogTitle>
          <DialogDescription>
            Carica i file Excel dei dati operativi. I file restano storicizzati
            come snapshot e alimentano i moduli M1-M5.
          </DialogDescription>
        </DialogHeader>

        {!canUpload ? (
          <UploadNonAutorizzato onClose={onClose} />
        ) : (
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2 max-h-[60vh] overflow-y-auto pr-1">
            {TIPI.map((t) => (
              <Dropzone key={t.slug} tipo={t} />
            ))}
            {/* Il piano del MES sta qui dal 27-08-2026. Prima si caricava solo da
                dentro il modulo Pianificazione, e chi carica i file non ha quel
                modulo: in produzione il piano è rimasto quattro mesi al 29 aprile
                mentre gli altri sei arrivavano ogni mattina. Occupa due colonne
                perché è l'unico che vuole due file insieme. */}
            <PianoMesDropzone />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UploadNonAutorizzato({ onClose: _onClose }: { onClose: () => void }) {
  return (
    <div className="rounded-lg border border-dashed bg-muted/20 p-6 space-y-3">
      <div className="flex items-start gap-3">
        <Info className="size-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="space-y-2 text-sm">
          <p className="font-medium">Upload non abilitato per il tuo profilo</p>
          <p className="text-muted-foreground">
            Il caricamento diretto dei file Excel è riservato a Maitime e ai
            profili abilitati. Se ti serve l'accesso, chiedilo a Maitime.
          </p>
          <p className="text-muted-foreground">
            Nel frattempo, se hai dati nuovi da caricare scrivici via{" "}
            <a href="/bacheca" className="text-primary hover:underline">
              Bacheca
            </a>{" "}
            e li carica Maitime per te.
          </p>
        </div>
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function Dropzone({ tipo }: { tipo: TipoConfig }) {
  const queryClient = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<M1Snapshot | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("tipo", tipo.slug);
      fd.append("file", file);
      return apiUploadFile<M1Snapshot>("/api/v1/dati/upload", fd);
    },
    onSuccess: (snap) => {
      setLastResult(snap);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ["m1"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "errore upload");
    },
  });

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      mutation.mutate(files[0]);
    },
    [mutation]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-lg border bg-card/40 p-3 space-y-2 transition-colors",
        isDragging && "border-brand-700 bg-brand-50/40",
        mutation.isPending && "opacity-60"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">{tipo.label}</h3>
          <p className="text-[11px] text-muted-foreground">{tipo.hint}</p>
        </div>
        <FileSpreadsheet className="size-4 text-muted-foreground shrink-0" />
      </div>

      <label
        className={cn(
          "block rounded-md border border-dashed px-3 py-4 text-center cursor-pointer hover:bg-accent/50",
          isDragging && "border-brand-700 bg-brand-50/60"
        )}
      >
        <input
          type="file"
          accept=".xls,.xlsx"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={mutation.isPending}
        />
        <Upload className="mx-auto size-4 text-muted-foreground mb-1" />
        <p className="text-[11px] text-muted-foreground">
          {mutation.isPending ? "Caricamento…" : "Trascina o clicca"}
        </p>
        <p className="text-[10px] text-muted-foreground/60 mt-0.5">
          {tipo.fileEsempio}
        </p>
      </label>

      {lastResult && (
        <div className="rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px] text-emerald-900 flex items-center gap-1.5">
          <CheckCircle2 className="size-3 shrink-0" />
          {lastResult.n_righe_ok} righe
          {lastResult.n_righe_err > 0 && ` · ${lastResult.n_righe_err} err`} ·{" "}
          {formatBytes(lastResult.dimensione_bytes)}
        </div>
      )}

      {errorMessage && (
        <div className="rounded bg-red-50 border border-red-200 px-2 py-1 text-[11px] text-red-900 flex items-center gap-1.5">
          <XCircle className="size-3 shrink-0" />
          {errorMessage}
        </div>
      )}
    </div>
  );
}

/**
 * Il piano del MES: due file insieme, non uno.
 *
 * Gli altri sei riquadri caricano appena si molla il file; questo no — finché
 * manca uno dei due export non c'è niente da mandare, e mandare il solo
 * confezionamento vorrebbe dire sostituire il piano con metà piano. Quindi due
 * caselle e un pulsante, che si accende quando ci sono entrambe.
 */
function PianoMesDropzone() {
  const [estr, setEstr] = useState<File | null>(null);
  const [conf, setConf] = useState<File | null>(null);
  const carica = useM2CaricaPiano();
  const pronto = Boolean(estr && conf) && !carica.isPending;

  return (
    <div className="md:col-span-2 rounded-lg border bg-card/40 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">Piano dal MES</h3>
          <p className="text-[11px] text-muted-foreground">
            i due export a 17 colonne · sostituisce il piano corrente
          </p>
        </div>
        <FileSpreadsheet className="size-4 text-muted-foreground shrink-0" />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <SlotFile etichetta="Estrusione" file={estr} onFile={setEstr} disabilitato={carica.isPending} />
        <SlotFile
          etichetta="Confezionamento"
          file={conf}
          onFile={setConf}
          disabilitato={carica.isPending}
        />
      </div>

      {carica.isError && (
        <div className="rounded bg-red-50 border border-red-200 px-2 py-1 text-[11px] text-red-900 flex items-start gap-1.5 dark:bg-red-950/50 dark:border-red-900 dark:text-red-300">
          <XCircle className="size-3 shrink-0 mt-0.5" />
          <span>
            {carica.error instanceof Error ? carica.error.message : "Caricamento fallito."}
          </span>
        </div>
      )}

      {carica.isSuccess ? (
        <div className="rounded bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px] text-emerald-900 flex items-start gap-1.5 dark:bg-emerald-950/50 dark:border-emerald-900 dark:text-emerald-300">
          <CheckCircle2 className="size-3 shrink-0 mt-0.5" />
          <span>
            Piano caricato: <b>{carica.data.kpi.lavorazioni_estrusione}</b> lavorazioni di
            estrusione e <b>{carica.data.kpi.lavorazioni_confezionamento}</b> di
            confezionamento.
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground/80">
            Le date verso Commesse e Spedizioni non si ricalcolano da qui.
          </p>
          <Button
            type="button"
            size="sm"
            disabled={!pronto}
            onClick={() => estr && conf && carica.mutate({ estrusione: estr, confezionamento: conf })}
          >
            {carica.isPending ? "Caricamento…" : "Carica il piano"}
          </Button>
        </div>
      )}
    </div>
  );
}

function SlotFile({
  etichetta,
  file,
  onFile,
  disabilitato,
}: {
  etichetta: string;
  file: File | null;
  onFile: (f: File | null) => void;
  disabilitato: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        onFile(e.dataTransfer.files?.[0] ?? null);
      }}
      className={cn(
        "block rounded-md border border-dashed px-3 py-3 text-center cursor-pointer hover:bg-accent/50",
        isDragging && "border-brand-700 bg-brand-50/60",
        file && "border-solid bg-card",
        disabilitato && "pointer-events-none opacity-60"
      )}
    >
      <input
        type="file"
        accept=".xls,.xlsx"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        disabled={disabilitato}
      />
      <p className="text-[11px] font-medium">{etichetta}</p>
      <p className="text-[10px] text-muted-foreground/80 mt-0.5 truncate">
        {file ? `${file.name} · ${formatBytes(file.size)}` : "Trascina o clicca"}
      </p>
    </label>
  );
}
